/**
 * The `screening_data` registry — labels, ordering, and value rendering for the AI's
 * structured extraction from a screening call.
 *
 * The webhook sender owns this schema, not us: it will grow keys we've never heard of. So the
 * rule everywhere below is *render what you find*. A key listed here gets a curated label and a
 * fixed position; a key that isn't listed is humanized and appended. Nothing is ever dropped —
 * a registry that silently hides new fields is a registry that quietly loses data.
 */

export const SCREENING_FIELDS = [
  { key: "current_role", label: "Current role" },
  { key: "current_company", label: "Current company" },
  { key: "current_location", label: "Location" },
  { key: "years_of_experience", label: "Experience" },
  { key: "current_ctc", label: "Current CTC" },
  { key: "expected_ctc", label: "Expected CTC" },
  { key: "joining_timeline", label: "Joining timeline" },
];

/** What a recruiter triages on. Everything else is off until they turn it on. */
export const DEFAULT_COLUMNS = ["current_location", "years_of_experience", "expected_ctc"];

const LABELS = Object.fromEntries(SCREENING_FIELDS.map((f) => [f.key, f.label]));

const humanizeKey = (key) => {
  const words = String(key).replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

export const fieldLabel = (key) =>
  Object.prototype.hasOwnProperty.call(LABELS, key) ? LABELS[key] : humanizeKey(key);

/**
 * The webhook doesn't omit a key it failed to capture — it sends this literal string instead.
 * Treated as equivalent to a missing value so it renders as "—" rather than as if it were an
 * answer. Matched whole-string only (case-insensitive, trimmed): a real answer that merely
 * contains the phrase, e.g. "no requirement for relocation", must still render verbatim.
 * Accepted risk: if the vendor ever changes the exact wording, this stops matching silently and
 * the raw sentinel text reappears in the UI instead of "—".
 */
const NO_VALUE_SENTINEL = "no requirement";

/**
 * Raw, exactly as the AI transcribed it — deliberately NOT currency-formatted.
 *
 * A candidate on a real call claimed an expected CTC of "500000000". Rendering that as
 * ₹5,00,00,000 makes an unvalidated transcription look like a verified figure. The ugly string
 * is the honest one: it reads as raw model output, which is what it is.
 */
export const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  if (typeof value === "string" && value.trim().toLowerCase() === NO_VALUE_SENTINEL) return "—";
  return String(value);
};

/** Known keys in registry order, then unknown keys alphabetically. */
const orderKeys = (keys) => {
  const present = new Set(keys);
  const known = SCREENING_FIELDS.filter((f) => present.has(f.key)).map((f) => f.key);
  const unknown = [...present]
    .filter((k) => !Object.prototype.hasOwnProperty.call(LABELS, k))
    .sort();
  return [...known, ...unknown];
};

/** One attempt's screening_data, ordered for display. Used by the panel. */
export const orderedEntries = (data) =>
  orderKeys(Object.keys(data || {})).map((key) => [key, data[key]]);

/**
 * The column picker's menu: every key this job actually captured, ordered.
 *
 * Derived from the data rather than from the registry, because screening questions vary by role —
 * offering a Sales job's "Expected CTC" column on a job that never asked would just produce a
 * column of dashes.
 */
export const availableFields = (screeningByResumeId) => {
  const seen = new Set();
  Object.values(screeningByResumeId || {}).forEach((entry) => {
    (entry?.results || []).forEach((result) => {
      Object.keys(result?.screening_data || {}).forEach((key) => seen.add(key));
    });
  });
  return orderKeys([...seen]).map((key) => ({ key, label: fieldLabel(key) }));
};
